import { z } from 'zod';

import type { ActivityDefinition } from './types.js';

import { correctAnswerIssues, duplicateValueIssues, feedbackIssues, optionIssues, requireOptions, trimmed, valuesOf } from './_checks.js';
import { draftFields, Option, optionText, questionFields } from './_shared.js';
import { activityIssue, asList, asRecord } from './types.js';

const issue = activityIssue('phrase_matching');

/** The dotted run an author writes into a sentence to mark where the blank goes. */
export const BLANK_MARKER = '....................';

/**
 * Fill in the blank. The runtime blanks the sentence one of two ways: the sentence
 * carries the dotted marker, or it says the correct option's label verbatim and that
 * word is blanked out. A sentence with neither renders whole, leaving nothing to fill —
 * the activity looks authored and is unplayable, so it is an error rather than a warning.
 */
export const phraseMatching: ActivityDefinition = {
  type: 'phrase_matching',
  summary: 'Fill in the blank in a sentence from a set of options.',
  setup: z.looseObject({
    ...draftFields,
    ...questionFields,
    sentence: z.string().optional(),
    options: z.array(Option).optional(),
    correctAnswer: z.string().optional(),
  }),
  check: (setup, ctx) => {
    const options = asList(setup.options);
    const issues = [
      ...requireOptions('phrase_matching', options, ctx),
      ...optionIssues('phrase_matching', options, ctx),
      ...duplicateValueIssues('phrase_matching', options, ctx),
      ...feedbackIssues('phrase_matching', setup.feedback, ctx),
    ];

    const sentence = trimmed(setup.sentence);
    const correct = trimmed(setup.correctAnswer);
    const values = valuesOf(options);

    if (!sentence) {
      issues.push(issue('noSentence', 'No sentence is written — there is nothing to fill in.', [...ctx.path, 'setup', 'sentence'], ctx));
    }

    const answerIssues = correctAnswerIssues({
      type: 'phrase_matching',
      field: 'correctAnswer',
      answers: [correct],
      values,
      ctx,
    });
    if (answerIssues.length) {
      // Without a correct answer that names an option there is no label to blank out, so
      // the blank check below would report a second, misleading failure.
      return [...issues, ...answerIssues];
    }

    const correctLabel = correct
      ? optionText(asRecord(options.find(option => (asRecord(option).value ?? asRecord(option).id) === correct)))
      : '';

    if (sentence && !sentence.includes(BLANK_MARKER) && !(correctLabel && sentence.includes(correctLabel))) {
      issues.push(issue(
        'noBlank',
        `The sentence has no blank in it — it neither carries the '${BLANK_MARKER}' marker nor says the correct answer${correctLabel ? ` ('${correctLabel}')` : ''} for the runtime to blank out.`,
        [...ctx.path, 'setup', 'sentence'],
        ctx,
        sentence,
      ));
    }

    return issues;
  },
};
